import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCity } from "@/context/CityContext";
import { toast } from "sonner";
import { X, Sparkles, Send, Calendar, MapPin, Ticket, Megaphone, Handshake, Palette, TrendingUp, Music, Users, Building2, Zap, Camera, ArrowUpRight, Mic, MicOff, Globe, PenLine, Search, Image as ImageIcon, Copy, Download, Loader2 } from "lucide-react";

/* ------------------------------------------------------------------ */
/* Context configs — prompts, suggestions and curated visual results   */
/* ------------------------------------------------------------------ */
const CONTEXTS = {
  fans: {
    heading: "What are you looking for?",
    sub: "Your AI for Events & Experiences",
    suggestions: ["Find events near me", "What's happening this weekend?", "Find concerts", "VIP after parties", "Find something under ₹1,000", "Where are my tickets?"],
  },
  organisers: {
    heading: "What can Genie help you with?",
    sub: "Your AI co-organiser",
    suggestions: ["Create my event", "Find artists", "Find sponsors", "Improve ticket sales", "Create a marketing plan", "What should I do next?"],
    cards: {
      "Create my event": [
        { icon: Ticket, title: "List Your Event", desc: "Live in minutes — tickets, QR check-in, payouts.", to: "/organizer", cta: "Start listing", match: 98 },
        { icon: Camera, title: "Add AI Photo Gallery", desc: "Auto-tagged fan photos linked to your event.", to: "/organisers", cta: "See how", match: 91 },
      ],
      "Find artists": [
        { icon: Music, title: "Artist Network", desc: "DJs, bands & performers open for bookings.", to: "/ecosystem", cta: "Browse artists", match: 95 },
        { icon: Users, title: "Post a Requirement", desc: "Let matched artists come to you.", to: "/ecosystem", cta: "Post now", match: 88 },
      ],
      "Find sponsors": [
        { icon: Handshake, title: "Sponsor Match", desc: "Brands actively looking for fests like yours.", to: "/brands-portal", cta: "Get matched", match: 96 },
        { icon: Building2, title: "Brand Activations", desc: "Booths, sampling & digital pushes for your crowd.", to: "/brands-portal", cta: "Explore", match: 89 },
      ],
      "Improve ticket sales": [
        { icon: TrendingUp, title: "Trending Boost", desc: "Get featured on the home feed & city rows.", to: "/organizer", cta: "Request boost", match: 93 },
        { icon: Zap, title: "Referral Engine", desc: "Fans earn ₹100/friend — your squad sells for you.", to: "/leaderboard", cta: "See leaderboard", match: 90 },
      ],
      "Create a marketing plan": [
        { icon: Megaphone, title: "3-Week Launch Plan", desc: "Tease → drop → last-call. Genie's proven cadence.", to: "/organisers", cta: "View playbook", match: 94 },
        { icon: Camera, title: "Content From Photos", desc: "Turn last edition's AI gallery into promo reels.", to: "/ai-gallery", cta: "Open gallery", match: 87 },
      ],
      "What should I do next?": [
        { icon: Ticket, title: "Publish Your Event", desc: "Your page is the funnel — get it live first.", to: "/organizer", cta: "Go to dashboard", match: 97 },
        { icon: Handshake, title: "Lock a Sponsor", desc: "Sponsors close faster with a live event page.", to: "/brands-portal", cta: "Find sponsors", match: 90 },
      ],
    },
  },
  brands: {
    heading: "What are you looking to achieve?",
    sub: "Your AI activation scout",
    suggestions: ["Find sponsorship opportunities", "Find college fests", "Find events for my audience", "Plan a brand activation", "Find relevant experiences"],
    cards: {
      "Find sponsorship opportunities": [
        { icon: Handshake, title: "Open Sponsorships", desc: "Fests & concerts actively seeking brand partners.", to: "/brands-portal", cta: "View openings", match: 96 },
        { icon: Users, title: "Gen-Z Reach Pack", desc: "Campus-first Gen-Z audiences across our live cities.", to: "/brands-portal", cta: "Get the pack", match: 92 },
      ],
      "Find college fests": [
        { icon: Sparkles, title: "College Fest Circuit", desc: "College fests on Eventom — filter by city & size.", to: "/", cta: "Browse fests", match: 97 },
        { icon: Building2, title: "Campus Ambassadors", desc: "On-ground student teams for your activation.", to: "/work-with-eventom", cta: "Learn more", match: 88 },
      ],
      "Find events for my audience": [
        { icon: Users, title: "Audience Match", desc: "Tell us your crowd — Genie maps the right events.", to: "/brands-portal", cta: "Start matching", match: 95 },
        { icon: TrendingUp, title: "Trending This Month", desc: "Highest-momentum events open to partners.", to: "/", cta: "See trending", match: 90 },
      ],
      "Plan a brand activation": [
        { icon: Zap, title: "Activation Builder", desc: "Booths, sampling, influencer tie-ups — end to end.", to: "/brands-portal", cta: "Plan it", match: 94 },
        { icon: Camera, title: "AI Photo Branding", desc: "Your logo on every fan's AI gallery photo.", to: "/brands-portal", cta: "See demo", match: 91 },
      ],
      "Find relevant experiences": [
        { icon: Sparkles, title: "Curated Experiences", desc: "Trips, house parties & pop-ups for niche audiences.", to: "/", cta: "Explore", match: 89 },
        { icon: Handshake, title: "Talk to Partnerships", desc: "A human + Genie combo for bigger plays.", to: "/brands-portal", cta: "Contact", match: 86 },
      ],
    },
  },
  ecosystem: {
    heading: "Find your next opportunity",
    sub: "For artists, creators, vendors & crews",
    suggestions: ["Find gigs", "Find events", "Find brands", "Find organisers", "Find collaborations"],
    cards: {
      "Find gigs": [
        { icon: Music, title: "Open Gigs", desc: "Events actively booking performers right now.", to: "/ecosystem", cta: "Browse gigs", match: 96 },
        { icon: Zap, title: "Get Verified", desc: "Verified profiles get 3× more organiser leads.", to: "/ecosystem", cta: "Verify me", match: 90 },
      ],
      "Find events": [
        { icon: Calendar, title: "Upcoming Events", desc: "See what's being planned in your city.", to: "/", cta: "View events", match: 94 },
        { icon: Camera, title: "Shoot For Eventom", desc: "Photographers wanted for AI galleries.", to: "/work-with-eventom", cta: "Apply", match: 89 },
      ],
      "Find brands": [
        { icon: Building2, title: "Brand Collabs", desc: "Brands hiring creators for event campaigns.", to: "/brands-portal", cta: "See briefs", match: 92 },
        { icon: Users, title: "Creator Network", desc: "Get listed and receive matched organiser leads.", to: "/ecosystem", cta: "Join now", match: 90 },
      ],
      "Find organisers": [
        { icon: Megaphone, title: "Organiser Directory", desc: "Pitch your service to active organisers.", to: "/ecosystem", cta: "Start pitching", match: 93 },
        { icon: Handshake, title: "Post Your Portfolio", desc: "Let organisers discover & book you.", to: "/ecosystem", cta: "Create profile", match: 91 },
      ],
      "Find collaborations": [
        { icon: Palette, title: "Collab Board", desc: "Creators × artists × venues — team up on drops.", to: "/community", cta: "See board", match: 88 },
        { icon: Sparkles, title: "Work With Eventom", desc: "Part-time, ambassador & full-time roles.", to: "/work-with-eventom", cta: "View roles", match: 90 },
      ],
    },
  },
};

const WORKING_STEPS = ["Understanding your request…", "Finding the best options…", "Personalising your results…"];
const MATCH_LABELS = ["Top match", "Great fit", "Trending pick", "Worth a look", "Fan favourite", "Fresh drop"];
const VIBE_GENRES = ["concert", "party", "comedy", "fest", "trip", "music", "rave", "open mic", "workshop", "sports", "fashion", "wedding", "cultural"];

const loadVibe = () => { try { return JSON.parse(localStorage.getItem("genie_vibe") || "{}"); } catch { return {}; } };

/* Deterministic intent → exact interface routing (AI only enriches results) */
const INTENTS = [
  { re: /(after ?-?part|house ?part|private ?part|\bvip\b)/i, icon: Sparkles, title: "VIP After Parties", desc: "Members-only after parties, house parties & trips — request access inside VIP Club.", to: "/vip", cta: "Open VIP Club" },
  { re: /((create|list|host|publish|organis|organiz)\w*[\s\S]{0,12}(event|show|fest|gig|concert))|list your event|sell tickets/i, icon: Megaphone, title: "List Your Event", desc: "Organiser dashboard — tickets, QR check-in and payouts.", to: "/organizer", cta: "Open Organiser Dashboard" },
  { re: /(sponsor|brand (deal|activation|partner)|activation|advertis)/i, icon: Building2, title: "Brand & Sponsor Hub", desc: "Submit a brief and get matched with the right events.", to: "/brands-portal", cta: "Open Brand Hub" },
  { re: /(list (my |your )?(service|work|business)|vendor|photograph|videograph|dj for hire|join\b.{0,20}(ecosystem|marketplace)|become a partner|partner with eventom|get listed)/i, icon: Users, title: "Join The Ecosystem", desc: "List your service — organisers and brands find you here.", to: "/ecosystem", cta: "List Yourself" },
  { re: /(my (ticket|pass|booking)|ticket (kahan|status)|show my qr|where('s| is| are)?\s*(my)?\s*(ticket|pass))/i, icon: Ticket, title: "My Tickets", desc: "Your QR passes and bookings are saved in your profile.", to: "/profile", cta: "Open My Tickets" },
  { re: /(leaderboard|referral)/i, icon: TrendingUp, title: "Squad Leaderboard", desc: "₹100 off per friend — see where you rank in your city.", to: "/leaderboard", cta: "See The Board" },
  { re: /(photo|gallery|\bpics?\b)/i, icon: Camera, title: "AI Photo Gallery", desc: "Find your event photos, tagged automatically.", to: "/ai-gallery", cta: "Open Gallery" },
  { re: /(contest|giveaway)/i, icon: Zap, title: "Contests", desc: "Live contests and giveaways on Eventom.", to: "/contests", cta: "See Contests" },
];
const detectIntent = (t) => INTENTS.find((x) => x.re.test(t)) || null;

const isEnded = (e) => {
  try {
    const d = e.end_date || e.date;
    if (!d) return false;
    const dt = new Date(d.includes("T") ? d : `${d}T23:59:59`);
    return dt < new Date();
  } catch { return false; }
};

const track = (kind, label, context, query) => {
  api.post("/genie/track", { kind, label, context, query }).catch(() => {});
};

function pickIndianVoice() {
  const voices = window.speechSynthesis?.getVoices?.() || [];
  return (
    voices.find((v) => v.lang === "en-IN" && /male|ravi|hemant|prabhat/i.test(v.name)) ||
    voices.find((v) => v.lang === "en-IN") ||
    voices.find((v) => /en[-_]GB/i.test(v.lang) && /male|daniel/i.test(v.name)) ||
    voices.find((v) => v.lang?.startsWith("en")) || null
  );
}

let _genieAudio = null;
let _genieAudioUrl = null;
async function speak(text) {
  // Premium ElevenLabs voice when configured server-side; browser voice fallback
  try {
    const res = await api.post("/genie/speak", { text }, { responseType: "blob", timeout: 20000 });
    try { _genieAudio?.pause(); } catch {}
    if (_genieAudioUrl) { try { URL.revokeObjectURL(_genieAudioUrl); } catch {} }
    _genieAudioUrl = URL.createObjectURL(res.data);
    _genieAudio = new Audio(_genieAudioUrl);
    _genieAudio.onended = () => { try { URL.revokeObjectURL(_genieAudioUrl); } catch {} _genieAudioUrl = null; };
    await _genieAudio.play();
    return;
  } catch {}
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const v = pickIndianVoice();
    if (v) u.voice = v;
    u.lang = "en-IN"; u.rate = 1.02; u.pitch = 0.92;
    window.speechSynthesis.speak(u);
  } catch {}
}

function rememberVibe(query, city) {
  const v = loadVibe();
  const ql = (query || "").toLowerCase();
  const found = VIBE_GENRES.filter((g) => ql.includes(g));
  if (found.length) v.genres = [...new Set([...(v.genres || []), ...found])].slice(-4);
  const m = ql.match(/under\s*₹?\s*([\d,.]+)/);
  if (m) v.budget = Number(m[1].replace(/[,.]/g, ""));
  if (city) v.city = city;
  try { localStorage.setItem("genie_vibe", JSON.stringify(v)); } catch {}
  return v;
}

function GenieAvatar({ size = 52 }) {
  return (
    <div style={{ width: size, height: size, position: "relative", flexShrink: 0 }}>
      <img src="/genie-bust.png" alt="Eventom Genie" width={size} height={size}
        className="genie-float" style={{ objectFit: "contain", borderRadius: 16 }} data-testid="genie-art-modal" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
export default function GenieModal({ open, onClose, context = "fans", initialQuery = "" }) {
  const cfg = CONTEXTS[context] || CONTEXTS.fans;
  const { city } = useCity();
  const [q, setQ] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | working | results
  const [mode, setMode] = useState("events"); // events | ask | write | imagine
  const [writeKind, setWriteKind] = useState("caption");
  const [imgAspect, setImgAspect] = useState("portrait");
  const [step, setStep] = useState(0);
  const [events, setEvents] = useState([]);
  const [webOpps, setWebOpps] = useState([]);
  const [actionCards, setActionCards] = useState([]);
  const [routeCard, setRouteCard] = useState(null);
  const [textReply, setTextReply] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [lastQuery, setLastQuery] = useState("");
  const [listening, setListening] = useState(false);
  const voiceUsed = useRef(false);
  const recRef = useRef(null);
  const ranInitial = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

  const toggleVoice = () => {
    if (!SR) return;
    if (listening) { recRef.current?.stop(); setListening(false); return; }
    const rec = new SR();
    rec.lang = "en-IN"; rec.interimResults = false;
    rec.onresult = (ev) => { const t = ev.results[0][0].transcript; voiceUsed.current = true; setQ(t); setListening(false); run(t); };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recRef.current = rec; rec.start(); setListening(true);
  };

  useEffect(() => () => { recRef.current?.stop(); try { window.speechSynthesis?.cancel(); _genieAudio?.pause(); } catch {} }, []);
  useEffect(() => { if (!open) { try { window.speechSynthesis?.cancel(); _genieAudio?.pause(); } catch {} } }, [open]);

  useEffect(() => {
    if (open) { setPhase("idle"); setQ(""); setEvents([]); setWebOpps([]); setActionCards([]); setRouteCard(null); setTextReply(""); setImageUrl(""); ranInitial.current = false; }
  }, [open, context]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open && initialQuery && !ranInitial.current) { ranInitial.current = true; run(initialQuery); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialQuery]);

  const fetchFanEvents = async (query) => {
    const vibe = loadVibe();
    const under1k = /under\s*₹?\s*1[,.]?000|under 1000/i.test(query);
    const generic = /near me|weekend|plan my|happening|something/i.test(query);
    const hints = generic && vibe.genres?.length ? ` — I usually like ${vibe.genres.join(", ")}${vibe.budget ? ` under ₹${vibe.budget}` : ""}` : "";
    try {
      const cityHint = city ? ` in ${city}` : vibe.city ? ` in ${vibe.city}` : "";
      const { data } = await api.post("/ai/search", { query: `${query}${cityHint}${hints}` });
      let items = (Array.isArray(data.results) ? data.results : (data.events || [])).filter((e) => !isEnded(e));
      const opps = (data.web_opportunities || []).slice(0, 4);
      if (!items.length && !opps.length) throw new Error("empty");
      return { items: items.slice(0, 6), opps };
    } catch {
      try {
        const { data } = await api.get(`/events?limit=60${city ? `&city=${encodeURIComponent(city)}` : ""}`);
        let list = (data || []).filter((e) => e.banner && !isEnded(e));
        if (under1k) list = list.filter((e) => { const p = (e.tiers || []).map((t) => Number(t.price || 0)); return p.length ? Math.min(...p) <= 1000 : true; });
        return { items: list.slice(0, 6), opps: [] };
      } catch { return { items: [], opps: [] }; }
    }
  };

  const run = async (query) => {
    const text = (query || q || "").trim();
    if (!text) return;
    if (context === "fans" && mode === "events") rememberVibe(text, city);
    track("prompt", text, `${context}:${mode}`, text);
    setLastQuery(text);
    setPhase("working"); setStep(0);
    setEvents([]); setWebOpps([]); setActionCards([]); setRouteCard(null); setTextReply(""); setImageUrl("");
    const stepTimer = setInterval(() => setStep((s) => Math.min(s + 1, 2)), 650);
    const minWait = new Promise((r) => setTimeout(r, mode === "imagine" ? 1200 : 1600));

    try {
      if (mode === "ask" || mode === "research") {
        const [{ data }] = await Promise.all([
          api.post("/genie/ask", { prompt: text, mode: mode === "research" ? "research" : "ask" }),
          minWait,
        ]);
        if (!mountedRef.current) { clearInterval(stepTimer); return; }
        setTextReply(data?.reply || "");
      } else if (mode === "write") {
        const [{ data }] = await Promise.all([
          api.post("/genie/write", { prompt: text, kind: writeKind, tone: "editorial" }),
          minWait,
        ]);
        if (!mountedRef.current) { clearInterval(stepTimer); return; }
        setTextReply(data?.reply || "");
      } else if (mode === "imagine") {
        const [{ data }] = await Promise.all([
          api.post("/genie/imagine", { prompt: text, aspect: imgAspect }),
          minWait,
        ]);
        if (!mountedRef.current) { clearInterval(stepTimer); return; }
        const u = data?.url || "";
        const abs = u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;
        setImageUrl(abs);
      } else if (context === "fans") {
        // Events mode for fans
        const intent = detectIntent(text);
        const [{ items, opps }] = await Promise.all([fetchFanEvents(text), minWait]);
        if (!mountedRef.current) { clearInterval(stepTimer); return; }
        setEvents(items); setWebOpps(opps); setRouteCard(intent);
        if (voiceUsed.current) {
          voiceUsed.current = false;
          if (items.length) {
            const top = items[0];
            speak(`I found ${items.length} picks for you. Top match is ${top.title}${top.city ? ` in ${top.city}` : ""}${top.date ? ` on ${new Date(top.date).toLocaleDateString("en-IN", { day: "numeric", month: "long" })}` : ""}. Tap a card to book.`);
          } else {
            speak("I couldn't find a match for that. Try another vibe.");
          }
        }
      } else {
        // Non-fan default: curated action cards
        const intent = detectIntent(text);
        const cards = cfg.cards?.[text]
          || (intent ? [{ icon: intent.icon, title: intent.title, desc: intent.desc, to: intent.to, cta: intent.cta, match: 99 }] : null)
          || cfg.cards?.[Object.keys(cfg.cards || {})[0]] || [];
        await minWait;
        if (!mountedRef.current) { clearInterval(stepTimer); return; }
        setActionCards(cards);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Genie hit a snag — try again";
      toast.error(typeof msg === "string" ? msg : "Genie hit a snag — try again");
    }
    clearInterval(stepTimer);
    setPhase("results");
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: "rgba(6,4,14,0.7)", backdropFilter: "blur(10px)" }}
      onClick={onClose} data-testid="genie-modal-overlay">
      <div onClick={(e) => e.stopPropagation()} data-testid="genie-modal"
        className="w-full sm:max-w-xl max-h-[88vh] overflow-y-auto no-scrollbar rounded-t-3xl sm:rounded-3xl genie-glow"
        style={{ background: "#141414", border: "1px solid #2A2A2A", animation: "genieSheetUp 0.35s cubic-bezier(0.2,0.9,0.3,1) both" }}>

        {/* Header */}
        <div className="flex items-center gap-3 p-5 pb-3 sticky top-0 z-10" style={{ background: "#141414", borderBottom: "1px solid #2A2A2A" }}>
          <GenieAvatar size={52} />
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-white flex items-center gap-1.5">Eventom Genie <Sparkles className="w-3.5 h-3.5 text-skyx" /></div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-skyx/80">{cfg.sub}</div>
          </div>
          <button onClick={onClose} data-testid="genie-modal-close" className="icon-circle !w-9 !h-9"><X className="w-4 h-4" /></button>
        </div>

        {/* Mode tabs */}
        <div className="px-5 pt-3">
          <div className="flex flex-wrap gap-1.5 bg-white/[0.03] border border-white/10 rounded-full p-1" data-testid="genie-mode-tabs">
            {[
              { k: "events", label: "Events", Icon: Ticket },
              { k: "ask", label: "Ask", Icon: Search },
              { k: "write", label: "Write", Icon: PenLine },
              // "Imagine" (AI image gen) retired for cost — see plan Phase 1 item 5
            ].map(({ k, label, Icon }) => (
              <button key={k} data-testid={`genie-mode-${k}`}
                onClick={() => { setMode(k); setPhase("idle"); setQ(""); setLastQuery(""); setTextReply(""); setImageUrl(""); setEvents([]); setWebOpps([]); setActionCards([]); setRouteCard(null); }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-heading font-bold uppercase tracking-widest transition-colors ${
                  mode === k ? "bg-lime text-ink shadow" : "text-white/70 hover:text-white"
                }`}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>
        </div>

        <div className="px-5 pb-6">
          {/* IDLE — prompt + suggestions */}
          {phase === "idle" && (
            <>
              {/* Purpose strip — helps first-time users understand WHY Genie exists.
                  Rendered only in idle state so it doesn't crowd active conversations. */}
              <div className="mt-2 rounded-2xl p-3 border border-lime/20 bg-gradient-to-r from-lime/[0.06] via-skyx/[0.04] to-magenta/[0.06]" data-testid="genie-why-strip">
                <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-lime mb-1.5">
                  <Sparkles className="w-3 h-3" /> Why Eventom Genie?
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { Icon: Ticket, label: "Skip the search — real events instantly" },
                    { Icon: PenLine, label: "Draft captions, invites, posts, emails" },
                    { Icon: ImageIcon, label: "Make posters & vibe images on the fly" },
                  ].map(({ Icon, label }, i) => (
                    <div key={i} className="flex flex-col gap-1">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-lg bg-white/10 text-white">
                        <Icon className="w-3 h-3" />
                      </span>
                      <div className="text-[10px] text-white/80 leading-snug">{label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="font-display text-2xl text-white mt-4">{
                mode === "ask" ? "Ask Genie anything." :
                mode === "write" ? "What should I write?" :
                mode === "imagine" ? "Describe an image." :
                cfg.heading
              }</div>
              {mode === "write" && (
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="genie-write-kinds">
                  {[
                    { k: "caption", label: "Caption" },
                    { k: "post", label: "Post" },
                    { k: "invite", label: "Invite" },
                    { k: "script", label: "Reel script" },
                    { k: "email", label: "Email" },
                    { k: "announcement", label: "Announcement" },
                    { k: "product", label: "Product · Store" },
                    { k: "discount", label: "Discount · Store" },
                  ].map((it) => (
                    <button key={it.k} onClick={() => setWriteKind(it.k)} data-testid={`genie-write-kind-${it.k}`}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest border transition-all ${
                        writeKind === it.k ? "bg-lime/15 border-lime/60 text-lime" : "bg-white/[0.04] border-white/15 text-white/70 hover:text-white"
                      }`}>{it.label}</button>
                  ))}
                </div>
              )}
              {mode === "imagine" && (
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="genie-imagine-aspects">
                  {[
                    { k: "portrait", label: "Portrait 4:5" },
                    { k: "square", label: "Square 1:1" },
                    { k: "landscape", label: "Landscape 16:9" },
                  ].map((it) => (
                    <button key={it.k} onClick={() => setImgAspect(it.k)} data-testid={`genie-aspect-${it.k}`}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest border transition-all ${
                        imgAspect === it.k ? "bg-magenta/15 border-magenta/60 text-magenta" : "bg-white/[0.04] border-white/15 text-white/70 hover:text-white"
                      }`}>{it.label}</button>
                  ))}
                </div>
              )}
              <form onSubmit={(e) => { e.preventDefault(); run(); }} className="mt-4 flex items-center gap-2">
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="genie-modal-input"
                  placeholder={
                    mode === "ask" ? "e.g. What should I do this Sunday in Patna?" :
                    mode === "write" ? "e.g. Open mic at Cafe 53, Sunday 7pm" :
                    mode === "imagine" ? "e.g. Neon nightclub poster with a lime accent" :
                    "Tell me what you're looking for…"
                  }
                  className="input-dark flex-1 !py-3 !text-sm" />
                {SR && context === "fans" && mode === "events" && (
                  <button type="button" onClick={toggleVoice} data-testid="genie-modal-voice"
                    title="Talk to Genie — Indian English voice replies"
                    className={`icon-circle !w-11 !h-11 shrink-0 ${listening ? "!bg-magenta text-white animate-pulse" : "text-skyx"}`}>
                    {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </button>
                )}
                <button disabled={!q.trim()} data-testid="genie-modal-submit" className="btn-orange !py-3 !px-4"><Send className="w-4 h-4" /></button>
              </form>
              {SR && context === "fans" && mode === "events" && <div className="mt-1.5 text-[9px] font-mono uppercase tracking-widest text-white/35">Tap the mic and talk — Genie replies with voice (Chrome/Edge)</div>}
              {mode === "events" && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {cfg.suggestions.map((s) => (
                    <button key={s} onClick={() => { setQ(s); run(s); }} data-testid={`genie-suggestion-${s.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      className="px-3.5 py-2 rounded-full text-xs font-body font-medium text-white/85 border border-skyx/30 bg-skyx/10 hover:bg-skyx/20 hover:border-skyx/60 transition-all active:scale-95">
                      {s}
                    </button>
                  ))}
                </div>
              )}
              {mode === "ask" && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {["What's happening in India this month?", "How do I plan a music night?", "What makes a Reel go viral?"].map((s) => (
                    <button key={s} onClick={() => { setQ(s); run(s); }} data-testid={`genie-ask-suggestion-${s.slice(0,10)}`}
                      className="px-3.5 py-2 rounded-full text-xs font-body font-medium text-white/85 border border-lime/30 bg-lime/10 hover:bg-lime/20 hover:border-lime/60 transition-all active:scale-95">{s}</button>
                  ))}
                </div>
              )}
              {mode === "write" && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {["College fest launch caption", "Comedy show WhatsApp invite", "Rooftop party 15s Reel"].map((s) => (
                    <button key={s} onClick={() => { setQ(s); run(s); }} data-testid={`genie-write-suggestion-${s.slice(0,10)}`}
                      className="px-3.5 py-2 rounded-full text-xs font-body font-medium text-white/85 border border-orangex/30 bg-orangex/10 hover:bg-orangex/20 hover:border-orangex/60 transition-all active:scale-95">{s}</button>
                  ))}
                </div>
              )}
              {mode === "imagine" && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {["Neon rooftop poster with Mumbai skyline", "Editorial cover for a Delhi comedy night", "Prism gradient DJ poster"].map((s) => (
                    <button key={s} onClick={() => { setQ(s); run(s); }} data-testid={`genie-imagine-suggestion-${s.slice(0,10)}`}
                      className="px-3.5 py-2 rounded-full text-xs font-body font-medium text-white/85 border border-magenta/30 bg-magenta/10 hover:bg-magenta/20 hover:border-magenta/60 transition-all active:scale-95">{s}</button>
                  ))}
                </div>
              )}
              {context === "fans" && mode === "events" && (() => {
                const v = loadVibe();
                const bits = [v.city, v.budget ? `under ₹${v.budget}` : null, ...(v.genres || [])].filter(Boolean);
                return bits.length > 0 ? (
                  <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-skyx/70" data-testid="genie-vibe-chip">
                    Tuned to your vibe: {bits.slice(0, 4).join(" · ")}
                    <button onClick={() => { localStorage.removeItem("genie_vibe"); setQ((x) => x); setPhase("idle"); }} className="ml-2 underline text-white/40 hover:text-white/70">reset</button>
                  </div>
                ) : null;
              })()}
            </>
          )}

          {/* WORKING — branded magic animation */}
          {phase === "working" && (
            <div className="py-10 flex flex-col items-center text-center" data-testid="genie-working">
              <div style={{ animation: "genieSpinPulse 1.6s ease-in-out infinite" }}><GenieAvatar size={84} /></div>
              <div className="mt-6 space-y-2">
                {WORKING_STEPS.map((s, i) => (
                  <div key={s} className={`text-sm font-body transition-all duration-300 ${i < step ? "text-skyx/50" : i === step ? "text-white" : "text-white/20"}`}>
                    {i === step ? <span className="inline-flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-lime animate-pulse" /> {s}</span> : <span>{s}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* RESULTS */}
          {phase === "results" && (
            <>
              <div className="flex items-center justify-between gap-3">
                <div className="font-display text-xl text-white">
                  {textReply ? (mode === "write" ? "Your copy is ready" : "Here's what I found") :
                    imageUrl ? "Fresh from Genie" :
                    events.length > 0 ? `Found ${events.length} ${events.length === 1 ? "pick" : "picks"} for you` :
                    routeCard ? "Here's where to go" :
                    actionCards.length > 0 ? "Here's what I'd do" : "Nothing yet"}
                </div>
                <button onClick={() => { setPhase("idle"); setQ(""); }} data-testid="genie-new-search" className="text-[10px] font-mono uppercase tracking-widest text-skyx underline shrink-0">New search</button>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mt-0.5 truncate">for "{lastQuery}"{city && context === "fans" ? ` · ${city}` : ""}</div>

              {/* Direct route card — exact interface for the detected intent */}
              {routeCard && (() => {
                const RIcon = routeCard.icon;
                return (
                  <Link to={routeCard.to} onClick={() => { track("card", `route:${routeCard.title}`, context, lastQuery); onClose(); }} data-testid="genie-route-card"
                    className="mt-4 flex items-center gap-3 rounded-2xl p-4 border border-lime/40 bg-lime/10 hover:bg-lime/15 hover:border-lime/70 transition-all">
                    <div className="w-10 h-10 rounded-xl bg-lime/15 border border-lime/40 flex items-center justify-center shrink-0"><RIcon className="w-5 h-5 text-lime" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-bold text-sm text-white">{routeCard.title}</div>
                      <div className="text-xs text-white/60">{routeCard.desc}</div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-widest text-lime shrink-0">{routeCard.cta} <ArrowUpRight className="w-3 h-3" /></span>
                  </Link>
                );
              })()}

              {/* Fan event cards */}
              {events.length > 0 && (
                <div className="mt-4 grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 gap-3" data-testid="genie-event-results">
                  {events.map((e, i) => {
                    const prices = (e.tiers || []).map((t) => Number(t.price || 0)).filter((p) => p > 0);
                    const minP = prices.length ? Math.min(...prices) : 0;
                    const href = e.slug ? `/e/${e.slug}` : `/events/${e.id || ""}`;
                    return (
                      <Link key={e.id || i} to={href} onClick={() => { track("card", e.title || "event", context, lastQuery); onClose(); }} data-testid={`genie-result-card-${i}`}
                        className="rounded-2xl overflow-hidden border border-white/12 bg-white/5 hover:border-lime/50 transition-all card-lift block">
                        <div className="relative aspect-[16/10] bg-ink">
                          {e.banner && <img src={e.banner} alt={e.title} className="w-full h-full object-cover" />}
                          <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-transparent" />
                          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-lime text-ink font-mono text-[9px] font-bold uppercase tracking-widest shadow-glow-lime">{MATCH_LABELS[i % MATCH_LABELS.length]}</span>
                          {minP > 0 && <span className="absolute bottom-2 right-2 font-mono font-bold text-sm text-lime drop-shadow">₹{minP}</span>}
                        </div>
                        <div className="p-3">
                          <div className="font-heading font-bold text-sm leading-tight text-white line-clamp-2">{e.title || e.name}</div>
                          <div className="mt-1.5 flex items-center gap-2.5 text-[9px] font-mono uppercase tracking-widest text-white/55">
                            {e.date && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3 text-skyx" /> {new Date(e.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</span>}
                            {e.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-magenta" /> {e.city}</span>}
                          </div>
                          <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-widest text-lime">View / Book <ArrowUpRight className="w-3 h-3" /></div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}

              {/* More from the web — external opportunities AFTER Eventom picks */}
              {webOpps.length > 0 && (
                <div className="mt-5" data-testid="genie-web-results">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-white/45 mb-2 inline-flex items-center gap-1.5"><Globe className="w-3 h-3 text-skyx" /> More from around the web</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {webOpps.map((w, i) => (
                      <a key={i} href={w.url} target="_blank" rel="noreferrer noopener"
                        onClick={() => track("web", `${w.source}: ${w.title}`, context, lastQuery)}
                        data-testid={`genie-web-card-${i}`}
                        className="rounded-2xl p-3.5 border border-white/10 bg-white/[0.04] hover:border-skyx/50 hover:bg-white/[0.07] transition-all block">
                        <div className="flex items-center justify-between gap-2">
                          <span className="px-2 py-0.5 rounded-full bg-skyx/15 border border-skyx/35 text-skyx font-mono text-[9px] font-bold uppercase tracking-widest">{w.source}</span>
                          <ArrowUpRight className="w-3.5 h-3.5 text-white/40" />
                        </div>
                        <div className="font-heading font-bold text-sm text-white mt-2 line-clamp-1">{w.title}</div>
                        {w.description && <div className="text-xs text-white/55 mt-0.5 line-clamp-2">{w.description}</div>}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Action / opportunity cards */}
              {actionCards.length > 0 && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="genie-action-results">
                  {actionCards.map((c, i) => (
                    <Link key={i} to={c.to} onClick={onClose} data-testid={`genie-action-card-${i}`}
                      className="rounded-2xl p-4 border border-purplex/35 bg-purplex/10 hover:border-lime/50 hover:bg-purplex/15 transition-all card-lift block">
                      <div className="flex items-start justify-between gap-2">
                        <div className="w-10 h-10 rounded-xl bg-skyx/15 border border-skyx/35 flex items-center justify-center"><c.icon className="w-5 h-5 text-skyx" /></div>
                        <span className="px-2 py-0.5 rounded-full bg-lime/15 border border-lime/40 text-lime font-mono text-[9px] font-bold">{c.match}% match</span>
                      </div>
                      <div className="font-heading font-bold text-sm text-white mt-2.5">{c.title}</div>
                      <div className="text-xs text-white/60 mt-1">{c.desc}</div>
                      <div className="mt-2.5 inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-widest text-lime">{c.cta} <ArrowUpRight className="w-3 h-3" /></div>
                    </Link>
                  ))}
                </div>
              )}

              {/* Text reply — Ask / Write / Research */}
              {textReply && (
                <div className="mt-4 rounded-2xl border border-lime/30 bg-lime/[0.05] p-4" data-testid="genie-text-reply">
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-lime inline-flex items-center gap-1.5">
                      {mode === "write" ? <PenLine className="w-3 h-3" /> : <Search className="w-3 h-3" />}
                      {mode === "write" ? `${writeKind} · ready to paste` : "Genie says"}
                    </div>
                    <button data-testid="genie-copy-text"
                      onClick={() => { navigator.clipboard.writeText(textReply); toast.success("Copied"); }}
                      className="text-[10px] font-mono uppercase tracking-widest text-white/70 hover:text-white inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
                  </div>
                  <div className="whitespace-pre-wrap text-sm text-white/90 leading-relaxed font-body">{textReply}</div>
                </div>
              )}

              {/* Image reply — Imagine */}
              {imageUrl && (
                <div className="mt-4 rounded-2xl border border-magenta/40 bg-magenta/[0.05] p-3" data-testid="genie-image-reply">
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-magenta inline-flex items-center gap-1.5">
                      <ImageIcon className="w-3 h-3" /> Generated · {imgAspect}
                    </div>
                    <a data-testid="genie-image-download" href={imageUrl} download="genie-image.png" target="_blank" rel="noreferrer"
                      className="text-[10px] font-mono uppercase tracking-widest text-white/70 hover:text-white inline-flex items-center gap-1"><Download className="w-3 h-3" /> Download</a>
                  </div>
                  <img src={imageUrl} alt="Genie generated" className="w-full rounded-xl border border-white/10" />
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button onClick={() => run(lastQuery)} data-testid="genie-image-regen" className="btn-ghost justify-center !py-2 !text-[11px]">Regenerate</button>
                    <button onClick={async () => {
                      try {
                        const res = await fetch(imageUrl);
                        const blob = await res.blob();
                        const file = new File([blob], "genie-image.png", { type: blob.type || "image/png" });
                        if (navigator.canShare && navigator.canShare({ files: [file] })) {
                          await navigator.share({ files: [file], title: "Genie image", text: "Made with Eventom Genie" });
                        } else {
                          toast("Long-press to save on mobile, or use Download.");
                        }
                      } catch { toast.error("Share unavailable"); }
                    }} data-testid="genie-image-share" className="btn-pink justify-center !py-2 !text-[11px]">Share</button>
                  </div>
                </div>
              )}

              {events.length === 0 && actionCards.length === 0 && webOpps.length === 0 && !routeCard && !textReply && !imageUrl && (
                <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-6 text-center" data-testid="genie-empty">
                  <div className="text-sm text-white/70">Genie couldn't find a match — try another vibe, or ask in the <Link to="/chat" onClick={onClose} className="text-lime underline">full chat</Link>.</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <style>{`
        @keyframes genieSheetUp { from { opacity: 0; transform: translateY(36px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes genieSpinPulse { 0%,100% { transform: scale(1) rotate(-3deg); } 50% { transform: scale(1.08) rotate(3deg); } }
        @keyframes genieFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
        @keyframes genieAura { 0%,100% { opacity: 0.65; transform: scale(1); } 50% { opacity: 1; transform: scale(1.07); } }
      `}</style>
    </div>,
    document.body
  );
}
